// src/pages/UserAgreementStatusPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Link,
  Select,
  Text,
  Spinner,
  Button,
  HStack,
  VStack,
  Checkbox,
  useToast,
  Tooltip,
  Divider,
  Alert,
  AlertIcon,
  IconButton,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tag,
  TagLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  StatGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { formatDateTime } from "../utils/dateUtils";
import { differenceInDays } from "date-fns";
import { useCognito } from "../utils/useCognito";
import {
  InfoOutlineIcon,
  EmailIcon,
  StarIcon,
  TimeIcon,
  WarningIcon,
  CheckCircleIcon,
} from "@chakra-ui/icons";
import { saveAs } from "file-saver";

const USERS_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getUser";
const AGREEMENT_PDF_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getSignedAgreementPdf";
const BAA_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData";
const IVR_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllIvrs";
const ORDERS_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders";

// ✅ New: actual email endpoint (admin_sendEmail)
const SEND_EMAIL_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/sendEmail";

// Hardcoded test accounts (default filter OFF)
const HARDCODED_TEST_EMAILS = new Set(
  [
    "ianlawrencemarlowe@gmail.com",
    "test_1754613110@example.com",
    "ian@curagenesis.com",
    "test_20250807_152720@example.com",
    "castelul1337@gmail.com",
    "ahmed@curagenesis.com",
    "chris@curagenesis.com",
    "ianmarlowe@gmail.com",
    "demo@curagenesis.com",
  ].map((e) => e.trim().toLowerCase())
);

const sectionIcons = {
  both: <CheckCircleIcon color="green.500" mr={2} />,
  baaOnly: <WarningIcon color="teal.500" mr={2} />,
  paOnly: <WarningIcon color="yellow.500" mr={2} />,
  none: <TimeIcon color="red.500" mr={2} />,
};

const sectionLabels = {
  both: "BAA + PA Signed",
  baaOnly: "BAA Signed Only",
  paOnly: "PA Signed Only",
  none: "Neither Signed",
};

const DEFAULT_EXPORT_HEADERS = [
  { key: "email", label: "Email" },
  { key: "UserId", label: "User ID" },
  { key: "createdAt", label: "Date Created" },
  { key: "baaSignedTime", label: "BAA Signed Time" },
  { key: "paSignedTime", label: "PA Signed Time" },
  { key: "orders", label: "Orders" },
  { key: "ivrCount", label: "IVRs Sent" },
];

function exportToCsv(filename, headers, rows) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const csv = [
    headers.map((h) => h.label).join(","),
    ...rows.map((row) => headers.map((h) => esc(row[h.key])).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, filename);
}

const UserAgreementStatusPage = () => {
  const toast = useToast();
  const { getIdToken } = useCognito();

  const [filter, setFilter] = useState("all");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [downloadingUserId, setDownloadingUserId] = useState(null);
  const [downloadingType, setDownloadingType] = useState(null);

  const [includeTestUsers, setIncludeTestUsers] = useState(false);
  const [testUsersFromApi, setTestUsersFromApi] = useState(null);
  const [testUsersLoading, setTestUsersLoading] = useState(false);

  const [sendingFor, setSendingFor] = useState(null); // userId currently sending email

  const [ivrs, setIvrs] = useState([]);
  const [ivrsLoading, setIvrsLoading] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Active Accounts state
  const [activeAccountsLoading, setActiveAccountsLoading] = useState(false);
  const [activeAccountsData, setActiveAccountsData] = useState({
    recent: [],
    inactive: [],
    never: [],
  });

  // Active Accounts sorting
  const [activeAccountsSortKey, setActiveAccountsSortKey] =
    useState("daysSinceLastOrder");
  const [activeAccountsSortDir, setActiveAccountsSortDir] = useState("asc");

  const {
    isOpen: isTestUsersOpen,
    onOpen: onTestUsersOpen,
    onClose: onTestUsersClose,
  } = useDisclosure();

  const now = new Date();

  // ===== Fetch users =====
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = getIdToken();
        const res = await fetch(USERS_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
        const result = await res.json();
        setUsers(result.users || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
        toast({
          title: "Failed to load users",
          description: "Please check network and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [getIdToken, toast]);

  // Normalize email / test users
  const isTestEmail = (email) => {
    if (!email) return false;
    const normalized = String(email).trim().toLowerCase();
    if (Array.isArray(testUsersFromApi) && testUsersFromApi.length > 0) {
      return new Set(
        testUsersFromApi.map((e) => e.trim().toLowerCase())
      ).has(normalized);
    }
    return HARDCODED_TEST_EMAILS.has(normalized);
  };

  // Filter users (default hide test)
  const filteredUsers = useMemo(() => {
    const base = includeTestUsers
      ? users
      : users.filter((u) => !isTestEmail(u.email));
    if (filter === "none") return base.filter((u) => !u.baaSigned && !u.paSigned);
    if (filter === "partial")
      return base.filter(
        (u) => (u.baaSigned && !u.paSigned) || (!u.baaSigned && u.paSigned)
      );
    return base;
  }, [users, filter, includeTestUsers, testUsersFromApi]);

  // Group by agreement status
  const grouped = useMemo(() => {
    const groups = { both: [], baaOnly: [], paOnly: [], none: [] };

    for (const user of filteredUsers) {
      const baa = user.baaSigned === true;
      const pa = user.paSigned === true;
      let sortTime;

      if (baa && pa) {
        sortTime = Math.max(
          new Date(user.baaSignedTime || 0).getTime(),
          new Date(user.paSignedTime || 0).getTime()
        );
        groups.both.push({ ...user, sortTime });
      } else if (baa && !pa) {
        sortTime = new Date(user.baaSignedTime || 0).getTime();
        groups.baaOnly.push({ ...user, sortTime });
      } else if (!baa && pa) {
        sortTime = new Date(user.paSignedTime || 0).getTime();
        groups.paOnly.push({ ...user, sortTime });
      } else {
        sortTime = new Date(user.createdAt || 0).getTime();
        groups.none.push({ ...user, sortTime });
      }
    }

    Object.keys(groups).forEach((k) =>
      groups[k].sort((a, b) => b.sortTime - a.sortTime)
    );
    if (filter === "none") return { none: groups.none };
    if (filter === "partial")
      return { baaOnly: groups.baaOnly, paOnly: groups.paOnly };
    return groups;
  }, [filteredUsers, filter]);

  // ===== Download Agreement PDFs =====
  const downloadAgreement = async (agreementType, userId, email) => {
    setDownloadingUserId(userId);
    setDownloadingType(agreementType);
    try {
      const token = getIdToken();
      const res = await fetch(AGREEMENT_PDF_API, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ agreementType, userId }),
      });
      const result = await res.json();
      if (result.downloadUrl) {
        const fileRes = await fetch(result.downloadUrl);
        if (!fileRes.ok) throw new Error("Failed to download file");
        const blob = await fileRes.blob();
        const safeEmail = (email || "unknown").replace(/[^a-z0-9@._-]/gi, "_");
        const safeUserId = String(userId || "unknown").replace(
          /[^a-z0-9._-]/gi,
          "_"
        );
        const safeType = String(agreementType || "agreement").replace(/\s+/g, "_");
        const filename = `${safeType}_${safeEmail}_${safeUserId}.pdf`;
        saveAs(blob, filename);
      } else {
        throw new Error(result.error || "No downloadUrl returned");
      }
    } catch (err) {
      console.error(`Failed to download ${agreementType}:`, err);
      toast({
        title: `Could not download ${agreementType}`,
        status: "error",
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setDownloadingUserId(null);
      setDownloadingType(null);
    }
  };

  // ===== IVRs =====
  useEffect(() => {
    const fetchIvrs = async () => {
      try {
        setIvrsLoading(true);
        const token = getIdToken();
        const res = await fetch(IVR_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        const parsed = JSON.parse(data.body);
        const allIvrs = Object.values(parsed.grouped || {}).flat();
        setIvrs(allIvrs);
      } catch (err) {
        console.error("Failed to fetch IVRs:", err);
        setIvrs([]);
      } finally {
        setIvrsLoading(false);
      }
    };
    fetchIvrs();
  }, [getIdToken]);

  const userIvrCountMap = useMemo(() => {
    const map = {};
    ivrs.forEach((ivr) => {
      const userId = ivr.userId || ivr.user_id || ivr.userID || "";
      if (userId) map[userId] = (map[userId] || 0) + 1;
      if (ivr.email) map[ivr.email] = (map[ivr.email] || 0) + 1;
    });
    return map;
  }, [ivrs]);

  const getIvrCount = (user) => {
    const userId = user.UserId || user.userId || "";
    if (userId && userIvrCountMap[userId] !== undefined) return userIvrCountMap[userId];
    if (user.email && userIvrCountMap[user.email] !== undefined)
      return userIvrCountMap[user.email];
    return 0;
    };

  // ===== Orders =====
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setOrdersLoading(true);
        const token = getIdToken();
        const res = await fetch(ORDERS_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        const allOrders = Object.values(data.ordersByUser || {}).flat();
        setOrders(allOrders);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };
    fetchOrders();
  }, [getIdToken]);

  const userOrderCountMap = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      const userId = order.userId || order.UserId || "";
      if (userId) map[userId] = (map[userId] || 0) + 1;
      if (order.userEmail) map[order.userEmail] = (map[order.userEmail] || 0) + 1;
    });
    return map;
  }, [orders]);

  const getOrderCount = (user) => {
    const userId = user.UserId || user.userId || "";
    if (userId && userOrderCountMap[userId] !== undefined)
      return userOrderCountMap[userId];
    if (user.email && userOrderCountMap[user.email] !== undefined)
      return userOrderCountMap[user.email];
    return 0;
  };

  // ===== Sorting (Agreement tab) =====
  const [sortKey, setSortKey] = useState("sortTime");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedGrouped = useMemo(() => {
    const sortFn = (a, b) => {
      let aVal = a[sortKey],
        bVal = b[sortKey];
      if (sortKey === "ivrCount") {
        aVal = getIvrCount(a);
        bVal = getIvrCount(b);
      }
      if (sortKey === "orderCount") {
        aVal = getOrderCount(a);
        bVal = getOrderCount(b);
      }
      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDir === "asc"
        ? (aVal ?? 0) - (bVal ?? 0)
        : (bVal ?? 0) - (aVal ?? 0);
    };
    const out = {};
    Object.entries(grouped).forEach(([k, arr]) => {
      out[k] = [...arr].sort(sortFn);
    });
    return out;
  }, [grouped, sortKey, sortDir]);

  // ===== CSV Modal (Agreement tab) =====
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [csvModalGroupKey, setCsvModalGroupKey] = useState("");
  const [csvModalHeaders, setCsvModalHeaders] = useState(DEFAULT_EXPORT_HEADERS);
  const [csvModalSelectedHeaders, setCsvModalSelectedHeaders] = useState(
    DEFAULT_EXPORT_HEADERS.map((h) => h.key)
  );
  const [csvModalFilter, setCsvModalFilter] = useState("all");

  const openCsvModal = (groupKey) => {
    setCsvModalGroupKey(groupKey);
    setCsvModalHeaders(DEFAULT_EXPORT_HEADERS);
    setCsvModalSelectedHeaders(DEFAULT_EXPORT_HEADERS.map((h) => h.key));
    setCsvModalFilter("all");
    setCsvModalOpen(true);
  };

  const handleExportCsv = () => {
    const groupUsers = sortedGrouped[csvModalGroupKey] || [];
    let rows = groupUsers.map((user) => ({
      email: user.email,
      UserId: user.UserId || user.userId,
      createdAt: formatDateTime(user.createdAt),
      baaSignedTime: user.baaSignedTime ? formatDateTime(user.baaSignedTime) : "",
      paSignedTime: user.paSignedTime ? formatDateTime(user.paSignedTime) : "",
      orders: getOrderCount(user),
      ivrCount: getIvrCount(user),
    }));
    if (csvModalFilter === "orders0") {
      rows = rows.filter((row) => Number(row.orders) === 0);
    }
    const headers = csvModalHeaders.filter((h) =>
      csvModalSelectedHeaders.includes(h.key)
    );
    exportToCsv(
      `users_${csvModalGroupKey}_${csvModalFilter}_${Date.now()}.csv`,
      headers,
      rows
    );
    setCsvModalOpen(false);
  };

  // ===== Active Accounts (fetch + map) =====
  const fetchActiveAccountsData = async () => {
    setActiveAccountsLoading(true);
    try {
      const token = getIdToken();

      const [ordersRes, baaRes] = await Promise.all([
        fetch(ORDERS_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }),
        fetch(BAA_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }),
      ]);

      const ordersData = await ordersRes.json();
      const baaData = await baaRes.json();

      const allOrders = Object.values(ordersData.ordersByUser || {}).flat();
      setOrders(allOrders);

      // Build facility map from BAA data
      const facilityMap = {};
      (baaData.baaData || []).forEach((baa) => {
        const userId = baa.UserId || baa.userId;
        if (!userId) return;

        let phoneNumber = baa.primaryContactPhone || "";
        if (!phoneNumber || phoneNumber.startsWith("000") || phoneNumber.startsWith("123")) {
          phoneNumber = baa.facilityPhone || "—";
        }

        let facilityAddress = baa.facilityAddress;
        if (typeof facilityAddress === "string") {
          try {
            facilityAddress = JSON.parse(facilityAddress);
          } catch {
            facilityAddress = {};
          }
        }
        const formattedAddress = facilityAddress
          ? [facilityAddress.street, facilityAddress.city, facilityAddress.state, facilityAddress.zip]
              .filter(Boolean)
              .join(", ")
          : "—";

        const npi = facilityAddress?.npi || baa.npi || "—";

        facilityMap[userId] = {
          facilityName: baa.facilityName || "Unknown Facility",
          phoneNumber: phoneNumber || "—",
          address: formattedAddress,
          npi: npi,
        };
      });

      const signedUsers = users
        .filter((u) => u.baaSigned && u.paSigned)
        .filter((u) => includeTestUsers || !isTestEmail(u.email));

      const userOrderMap = {};
      allOrders.forEach((order) => {
        const userId = order.userId || order.UserId;
        if (!userId) return;
        if (!userOrderMap[userId]) userOrderMap[userId] = [];
        userOrderMap[userId].push(order);
      });

      const recent = [];
      const inactive = [];
      const never = [];
      const now = new Date();

      signedUsers.forEach((user) => {
        const userId = user.UserId || user.userId;
        const userOrders = userOrderMap[userId] || [];
        const facilityInfo = facilityMap[userId] || {
          facilityName: "Unknown Facility",
          phoneNumber: "—",
          address: "—",
          npi: "—",
        };

        if (userOrders.length === 0) {
          const daysSinceSigned = Math.max(
            differenceInDays(new Date(), new Date(user.baaSignedTime || 0)),
            differenceInDays(new Date(), new Date(user.paSignedTime || 0))
          );
          never.push({
            ...user,
            facilityName: facilityInfo.facilityName,
            phoneNumber: facilityInfo.phoneNumber,
            address: facilityInfo.address,
            npi: facilityInfo.npi,
            orderCount: 0,
            lastOrderDate: null,
            daysSinceLastOrder: null,
            daysSinceSigned: daysSinceSigned,
          });
        } else {
          const sortedOrders = userOrders.sort(
            (a, b) =>
              new Date(b.updatedAt || b.createdAt || 0) -
              new Date(a.updatedAt || a.createdAt || 0)
          );
          const lastOrder = sortedOrders[0];
          const lastOrderDate = new Date(lastOrder.updatedAt || lastOrder.createdAt);
          const daysSinceLastOrder = differenceInDays(now, lastOrderDate);

          const userData = {
            ...user,
            facilityName: facilityInfo.facilityName,
            phoneNumber: facilityInfo.phoneNumber,
            address: facilityInfo.address,
            npi: facilityInfo.npi,
            orderCount: userOrders.length,
            lastOrderDate: lastOrderDate,
            daysSinceLastOrder: daysSinceLastOrder,
            lastOrderId: lastOrder.orderId,
          };

          if (daysSinceLastOrder <= 30) recent.push(userData);
          else inactive.push(userData);
        }
      });

      recent.sort((a, b) => a.daysSinceLastOrder - b.daysSinceLastOrder);
      inactive.sort((a, b) => a.daysSinceLastOrder - b.daysSinceLastOrder);
      never.sort((a, b) => a.daysSinceSigned - b.daysSinceSigned);

      setActiveAccountsData({ recent, inactive, never });
    } catch (err) {
      console.error("Failed to fetch active accounts data:", err);
      toast({
        title: "Failed to load active accounts",
        description: "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActiveAccountsLoading(false);
    }
  };

  const handleActiveAccountsSort = (key) => {
    if (activeAccountsSortKey === key) {
      setActiveAccountsSortDir(activeAccountsSortDir === "asc" ? "desc" : "asc");
    } else {
      setActiveAccountsSortKey(key);
      setActiveAccountsSortDir("asc");
    }
  };

  const sortedActiveAccountsData = useMemo(() => {
    const sortFn = (a, b) => {
      let aVal = a[activeAccountsSortKey];
      let bVal = b[activeAccountsSortKey];

      if (activeAccountsSortKey === "facilityName") {
        aVal = aVal || "—";
        bVal = bVal || "—";
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return activeAccountsSortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return activeAccountsSortDir === "asc"
        ? (aVal ?? 0) - (bVal ?? 0)
        : (bVal ?? 0) - (aVal ?? 0);
    };

    return {
      recent: [...activeAccountsData.recent].sort(sortFn),
      inactive: [...activeAccountsData.inactive].sort(sortFn),
      never: [...activeAccountsData.never].sort(sortFn),
    };
  }, [activeAccountsData, activeAccountsSortKey, activeAccountsSortDir]);

  // ===== Email helpers (new) =====
  const sendAdminEmail = async ({ to, template, data, attachment }) => {
    const token = getIdToken();
    const res = await fetch(SEND_EMAIL_API, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(
        attachment
          ? { to, template, data, attachment }
          : { to, template, data }
      ),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json?.success) {
      throw new Error(json?.message || json?.error || "Failed to send email");
    }
    return json;
  };

  /**
   * Map menu click → (template, data) for current context.
   * - Agreement groups ("none" / "baaOnly" / "paOnly"): use BAA_PA_Nudge
   * - Active Accounts (recent/inactive/never) and both-signed group: use NoOrders30
   */
  const resolveEmailTemplate = (user, type, contextKey) => {
    const email = user?.email;
    const firstName = user?.firstName; // if present; otherwise template prints "there"

    // If user is missing agreements → BAA_PA_Nudge
    const isMissingAgreement =
      contextKey === "none" || contextKey === "baaOnly" || contextKey === "paOnly";

    if (isMissingAgreement) {
      return {
        template: "BAA_PA_Nudge",
        data: {
          firstName,
          needsBAA: !user.baaSigned,
          needsPA: !user.paSigned,
        },
        to: email,
      };
    }

    // Otherwise, use NoOrders30 (re-engagement / demo / help / check-in)
    // lastOrderDate if available, else rely on daysSinceSigned if present (never)
    let lastOrderDateStr = "";
    let daysSince;

    if (user.lastOrderDate) {
      lastOrderDateStr = formatDateTime(user.lastOrderDate);
      daysSince = user.daysSinceLastOrder;
    } else if (typeof user.daysSinceSigned === "number") {
      lastOrderDateStr = null;
      daysSince = user.daysSinceSigned;
    }

    return {
      template: "NoOrders30",
      data: {
        firstName,
        lastOrderDate: lastOrderDateStr || undefined,
        daysSince: typeof daysSince === "number" ? daysSince : undefined,
      },
      to: email,
    };
  };

  // Single entry point for all email menu items
  const handleSendEmailDropdown = async (user, type, contextKey) => {
    const userId = user.UserId || user.userId;
    if (!user?.email) {
      toast({
        title: "Missing email",
        description: "This user does not have an email address.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    const { template, data, to } = resolveEmailTemplate(user, type, contextKey);

    // Optional confirm (feel free to remove if you want one-click)
    const labels = {
      reminder: "send a signing reminder",
      help: "offer help",
      demo: "offer a demo",
      check: "send a quick check-in",
    };
    const verb = labels[type] || "send an email";
    if (!window.confirm(`Okay to ${verb} to ${user.email}?`)) return;

    try {
      setSendingFor(userId);
      await sendAdminEmail({ to, template, data });
      toast({
        title: "Email sent",
        description: `Template "${template}" sent to ${user.email}.`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (err) {
      console.error("Send email failed:", err);
      toast({
        title: "Failed to send email",
        description: err.message || "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingFor(null);
    }
  };

  // ===== Manage Test Users (modal stub preserved) =====
  const openManageTestUsers = async () => {
    setTestUsersLoading(true);
    onTestUsersOpen();
    try {
      // If you wire a backend later, fetch into setTestUsersFromApi here.
      setTestUsersFromApi(Array.from(HARDCODED_TEST_EMAILS));
      toast({
        title: "Using fallback test users",
        description: "Backend not ready yet; showing hardcoded list.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setTestUsersLoading(false);
    }
  };

  if (loading) {
    return (
      <Box p={8}>
        <Heading mb={4}>User Agreement Status</Heading>
        <Spinner size="xl" />
      </Box>
    );
  }

  // Summary stats
  const total = filteredUsers.length;
  const totals = {
    both: grouped.both?.length || 0,
    baaOnly: grouped.baaOnly?.length || 0,
    paOnly: grouped.paOnly?.length || 0,
    none: grouped.none?.length || 0,
  };

  // ===== Active Accounts tab renderer =====
  const renderActiveAccountsTab = () => {
    if (activeAccountsLoading) {
      return (
        <VStack spacing={4} py={8}>
          <Spinner size="xl" />
          <Text>Loading active accounts data...</Text>
        </VStack>
      );
    }

    const { recent, inactive, never } = sortedActiveAccountsData;
    const total = recent.length + inactive.length + never.length;

    const renderSortHeader = (label, key) => (
      <Th cursor="pointer" onClick={() => handleActiveAccountsSort(key)}>
        {label}{" "}
        {activeAccountsSortKey === key &&
          (activeAccountsSortDir === "asc" ? "▲" : "▼")}
      </Th>
    );

    const Section = ({ title, color, data, keyName, helper, emptyAlert }) => (
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="md" color={color}>
            {title} ({data.length})
          </Heading>
          <HStack>
            <Text fontSize="sm" color="gray.600">
              {helper}
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme={color.includes("green") ? "green" : color.includes("yellow") ? "yellow" : "red"}
              onClick={() => exportActiveAccountsCsv(keyName, data, title)}
              isDisabled={data.length === 0}
            >
              Export CSV
            </Button>
          </HStack>
        </HStack>

        {data.length === 0 ? (
          <Alert status={keyName === "inactive" || keyName === "never" ? "success" : "info"}>
            <AlertIcon />
            {emptyAlert}
          </Alert>
        ) : (
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                {renderSortHeader("Email", "email")}
                {renderSortHeader("Facility", "facilityName")}
                {renderSortHeader("Phone", "phoneNumber")}
                {renderSortHeader("Order Count", "orderCount")}
                {renderSortHeader("Last Order", "lastOrderDate")}
                {renderSortHeader("Days Ago", "daysSinceLastOrder")}
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((user) => (
                <Tr key={user.UserId || user.userId}>
                  <Td>{user.email}</Td>
                  <Td>{user.facilityName || "—"}</Td>
                  <Td>{user.phoneNumber || "—"}</Td>
                  <Td>
                    <Badge colorScheme="blue">{user.orderCount}</Badge>
                  </Td>
                  <Td>{user.lastOrderDate ? formatDateTime(user.lastOrderDate) : "—"}</Td>
                  <Td>
                    <Badge
                      colorScheme={
                        user.daysSinceLastOrder != null
                          ? user.daysSinceLastOrder > 90
                            ? "red"
                            : user.daysSinceLastOrder > 30
                            ? "yellow"
                            : "green"
                          : "gray"
                      }
                    >
                      {user.daysSinceLastOrder ?? "—"}
                    </Badge>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} size="sm" variant="outline">
                        Actions
                      </MenuButton>
                      <MenuList>
                        <MenuItem
                          icon={<EmailIcon color="blue.500" />}
                          onClick={() => handleSendEmailDropdown(user, "reminder", keyName)}
                        >
                          Re-engagement Email
                        </MenuItem>
                        <MenuItem
                          icon={<StarIcon color="yellow.500" />}
                          onClick={() => handleSendEmailDropdown(user, "demo", keyName)}
                        >
                          Offer Demo
                        </MenuItem>
                        <MenuItem
                          icon={<InfoOutlineIcon color="teal.500" />}
                          onClick={() => handleSendEmailDropdown(user, "help", keyName)}
                        >
                          Offer Help
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    );

    return (
      <VStack spacing={6} align="stretch">
        {/* Test Account Filter */}
        <HStack mb={4} wrap="wrap" gap={4}>
          <Checkbox
            isChecked={includeTestUsers}
            onChange={(e) => {
              setIncludeTestUsers(e.target.checked);
              setActiveAccountsData({ recent: [], inactive: [], never: [] });
            }}
          >
            Include test accounts
          </Checkbox>
          <Text fontSize="sm" color="gray.600">
            {includeTestUsers
              ? "Showing all users including test accounts"
              : "Test accounts are hidden"}
          </Text>
        </HStack>

        {/* Stats */}
        <StatGroup>
          <Stat>
            <StatLabel>Recent Orders (≤30 days)</StatLabel>
            <StatNumber color="green.500">{recent.length}</StatNumber>
            <StatHelpText>Active customers</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Inactive (&gt;30 days)</StatLabel>
            <StatNumber color="yellow.500">{inactive.length}</StatNumber>
            <StatHelpText>Need re-engagement</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Never Ordered</StatLabel>
            <StatNumber color="red.500">{never.length}</StatNumber>
            <StatHelpText>Potential leads</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Total Signed Users</StatLabel>
            <StatNumber>{total}</StatNumber>
            <StatHelpText>BAA + PA completed</StatHelpText>
          </Stat>
        </StatGroup>

        <Divider />

        <Section
          title="Recent Orders"
          color="green.600"
          data={recent}
          keyName="recent"
          helper="Users who ordered within the last 30 days"
          emptyAlert="No users have ordered within the last 30 days."
        />

        <Divider />

        <Section
          title="Inactive Users"
          color="yellow.600"
          data={inactive}
          keyName="inactive"
          helper="Users whose last order was more than 30 days ago"
          emptyAlert="All users with orders are still active!"
        />

        <Divider />

        <Box>
          <HStack justify="space-between" mb={4}>
            <Heading size="md" color="red.600">
              Never Ordered ({never.length})
            </Heading>
            <HStack>
              <Text fontSize="sm" color="gray.600">
                Users with signed agreements but no orders
              </Text>
              <Button
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={() => exportActiveAccountsCsv("never", never, "Never Ordered")}
                isDisabled={never.length === 0}
              >
                Export CSV
              </Button>
            </HStack>
          </HStack>

          {never.length === 0 ? (
            <Alert status="success">
              <AlertIcon />
              All users with signed agreements have placed orders!
            </Alert>
          ) : (
            <>
              <Alert status="warning" mb={4} borderRadius="md">
                <AlertIcon />
                These users have completed both BAA and PA but haven't placed any orders
                yet. Consider reaching out to help them get started.
              </Alert>

              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Email</Th>
                    <Th>Facility</Th>
                    <Th>Phone</Th>
                    <Th>Date Signed</Th>
                    <Th>Days Since Signed</Th>
                    <Th>IVRs Sent</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {never.map((user) => (
                    <Tr key={user.UserId || user.userId}>
                      <Td>{user.email}</Td>
                      <Td>{user.facilityName || "—"}</Td>
                      <Td>{user.phoneNumber || "—"}</Td>
                      <Td>
                        {formatDateTime(
                          Math.max(
                            new Date(user.baaSignedTime || 0),
                            new Date(user.paSignedTime || 0)
                          )
                        )}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={
                            user.daysSinceSigned > 60
                              ? "red"
                              : user.daysSinceSigned > 30
                              ? "yellow"
                              : "green"
                          }
                        >
                          {user.daysSinceSigned}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme="purple">{getIvrCount(user)}</Badge>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton as={Button} size="sm" variant="outline">
                            Actions
                          </MenuButton>
                          <MenuList>
                            <MenuItem
                              icon={<StarIcon color="yellow.500" />}
                              onClick={() => handleSendEmailDropdown(user, "demo", "never")}
                            >
                              Offer Demo
                            </MenuItem>
                            <MenuItem
                              icon={<InfoOutlineIcon color="teal.500" />}
                              onClick={() => handleSendEmailDropdown(user, "help", "never")}
                            >
                              Offer Help
                            </MenuItem>
                            <MenuItem
                              icon={<EmailIcon color="blue.500" />}
                              onClick={() => handleSendEmailDropdown(user, "reminder", "never")}
                            >
                              Re-engagement Email
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </>
          )}
        </Box>
      </VStack>
    );
  };

  // ===== Render =====
  return (
    <Box p={8}>
      <HStack justify="space-between" align="start" mb={4} wrap="wrap" gap={4}>
        <Heading>User Agreement Status</Heading>
        <HStack>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<InfoOutlineIcon />}
            onClick={openManageTestUsers}
            _hover={{ transform: "scale(1)" }}
          >
            Manage Test Users
          </Button>
        </HStack>
      </HStack>

      <Tabs variant="enclosed" colorScheme="teal">
        <TabList>
          <Tab>Agreement Status</Tab>
          <Tab
            onClick={() => {
              if (
                activeAccountsData.recent.length === 0 &&
                activeAccountsData.inactive.length === 0 &&
                activeAccountsData.never.length === 0
              ) {
                fetchActiveAccountsData();
              }
            }}
          >
            Active Accounts
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            <Alert status="info" mb={4} borderRadius="lg">
              <AlertIcon />
              <Box>
                <Text mb={1}>
                  Users are grouped by agreement status. You can download BAA/PA PDFs or send
                  follow-up emails using our admin email service (with access check & audit log).
                </Text>
                <HStack spacing={3}>
                  <Tag colorScheme="green">
                    <TagLabel>Both Signed: {totals.both}</TagLabel>
                  </Tag>
                  <Tag colorScheme="teal">
                    <TagLabel>BAA Only: {totals.baaOnly}</TagLabel>
                  </Tag>
                  <Tag colorScheme="yellow">
                    <TagLabel>PA Only: {totals.paOnly}</TagLabel>
                  </Tag>
                  <Tag colorScheme="red">
                    <TagLabel>Neither: {totals.none}</TagLabel>
                  </Tag>
                  <Text color="gray.500">Total shown: {total}</Text>
                </HStack>
              </Box>
            </Alert>

            <HStack mb={4} wrap="wrap" gap={4}>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                width="240px"
              >
                <option value="all">Show All</option>
                <option value="none">Neither Signed</option>
                <option value="partial">BAA or PA Missing</option>
              </Select>

              <Checkbox
                isChecked={includeTestUsers}
                onChange={(e) => setIncludeTestUsers(e.target.checked)}
              >
                Include test accounts
              </Checkbox>
            </HStack>

            <Divider mb={6} />

            {ivrsLoading && (
              <Box mb={4}>
                <Spinner size="sm" mr={2} />
                <Text as="span" fontSize="sm" color="gray.500">
                  Loading IVR counts…
                </Text>
              </Box>
            )}
            {ordersLoading && (
              <Box mb={4}>
                <Spinner size="sm" mr={2} />
                <Text as="span" fontSize="sm" color="gray.500">
                  Loading order counts…
                </Text>
              </Box>
            )}

            {Object.entries(sortedGrouped).map(([groupKey, groupUsers]) => (
              <Box key={groupKey} mb={10}>
                <HStack mb={2} justify="space-between">
                  <HStack>
                    {sectionIcons[groupKey]}
                    <Heading size="md">
                      {sectionLabels[groupKey]} ({groupUsers.length})
                    </Heading>
                    {groupKey === "none" && (
                      <Tooltip label="Older accounts with neither agreement signed — consider outreach." hasArrow>
                        <InfoOutlineIcon color="gray.500" />
                      </Tooltip>
                    )}
                  </HStack>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={() => openCsvModal(groupKey)}
                    _hover={{ transform: "scale(1)" }}
                  >
                    Create CSV
                  </Button>
                </HStack>

                {groupKey === "none" && groupUsers.length > 0 && (
                  <Alert status="warning" mb={3} borderRadius="md">
                    <AlertIcon />
                    These users have neither agreement signed. Send a signing reminder or offer help.
                  </Alert>
                )}

                {(groupKey === "baaOnly" || groupKey === "paOnly") &&
                  groupUsers.length > 0 && (
                    <Alert status="info" mb={3} borderRadius="md">
                      <AlertIcon />
                      These users are missing one agreement. Send a signing reminder or offer help.
                    </Alert>
                  )}

                <Table variant="simple" size="md" borderWidth={1} minWidth="900px" maxWidth="100%">
                  <Thead bg="gray.100" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th cursor="pointer" onClick={() => handleSort("email")}>
                        Email {sortKey === "email" && (sortDir === "asc" ? "▲" : "▼")}
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort("UserId")}>
                        User ID {sortKey === "UserId" && (sortDir === "asc" ? "▲" : "▼")}
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort("createdAt")}>
                        Date Created {sortKey === "createdAt" && (sortDir === "asc" ? "▲" : "▼")}
                      </Th>
                      <Th cursor="pointer" onClick={() => handleSort("sortTime")}>
                        Agreements {sortKey === "sortTime" && (sortDir === "asc" ? "▲" : "▼")}
                      </Th>
                      <Th>Documents</Th>
                      {groupKey === "both" && (
                        <>
                          <Th cursor="pointer" onClick={() => handleSort("ivrCount")}>
                            IVRs Sent {sortKey === "ivrCount" && (sortDir === "asc" ? "▲" : "▼")}
                          </Th>
                          <Th cursor="pointer" onClick={() => handleSort("orderCount")}>
                            Orders {sortKey === "orderCount" && (sortDir === "asc" ? "▲" : "▼")}
                          </Th>
                        </>
                      )}
                      <Th isNumeric>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {groupUsers.map((user, index) => {
                      const baa = user.baaSigned === true;
                      const pa = user.paSigned === true;
                      const createdAt = new Date(user.createdAt);
                      const ageDays = differenceInDays(now, createdAt);
                      const rowBg = index % 2 === 0 ? "white" : "blue.50";
                      const userId = user.UserId || user.userId;
                      const baaDays = baa ? 0 : differenceInDays(now, new Date(user.baaSignedTime || user.createdAt));
                      const paDays = pa ? 0 : differenceInDays(now, new Date(user.paSignedTime || user.createdAt));
                      const maxDays = !baa && !pa ? ageDays : Math.max(baaDays, paDays);

                      return (
                        <Tr key={userId} bg={rowBg}>
                          <Td>{user.email}</Td>
                          <Td>{userId}</Td>
                          <Td>{formatDateTime(user.createdAt)}</Td>
                          <Td>
                            <HStack spacing={2}>
                              {groupKey === "both" && (
                                <>
                                  <Badge colorScheme="green">
                                    BAA {formatDateTime(user.baaSignedTime)}
                                  </Badge>
                                  <Badge colorScheme="green">
                                    PA {formatDateTime(user.paSignedTime)}
                                  </Badge>
                                </>
                              )}
                              {!baa && (
                                <Badge colorScheme="red" variant="outline">
                                  {baaDays}d
                                </Badge>
                              )}
                              {!pa && (
                                <Badge colorScheme="yellow" variant="outline">
                                  {paDays}d
                                </Badge>
                              )}
                              {!baa && !pa && (
                                <Badge colorScheme="orange" variant="solid">
                                  {maxDays} days no signatures
                                </Badge>
                              )}
                              {!baa && pa && (
                                <Badge colorScheme="red">PA signed before BAA — check</Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <HStack spacing={3}>
                              {baa && (
                                <Link
                                  color="teal.500"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    downloadAgreement("BAA", userId, user.email);
                                  }}
                                >
                                  BAA{" "}
                                  {downloadingUserId === userId &&
                                    downloadingType === "BAA" && <Spinner size="xs" ml={1} />}
                                </Link>
                              )}
                              {pa && (
                                <Link
                                  color="blue.500"
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    downloadAgreement("Purchase Agreement", userId, user.email);
                                  }}
                                >
                                  PA{" "}
                                  {downloadingUserId === userId &&
                                    downloadingType === "Purchase Agreement" && (
                                      <Spinner size="xs" ml={1} />
                                    )}
                                </Link>
                              )}
                            </HStack>
                          </Td>
                          {groupKey === "both" && (
                            <>
                              <Td>
                                <HStack>
                                  <StarIcon color="yellow.400" mr={1} />
                                  <Text>{getIvrCount(user)}</Text>
                                </HStack>
                              </Td>
                              <Td>
                                <Badge colorScheme="blue">{getOrderCount(user)}</Badge>
                              </Td>
                            </>
                          )}
                          <Td isNumeric>
                            <Menu>
                              <MenuButton
                                as={Button}
                                size="sm"
                                leftIcon={<EmailIcon />}
                                _hover={{ transform: "scale(1)" }}
                                isLoading={sendingFor === userId}
                              >
                                Email
                              </MenuButton>
                              <MenuList>
                                {(groupKey === "none" ||
                                  groupKey === "baaOnly" ||
                                  groupKey === "paOnly") && (
                                  <>
                                    <MenuItem
                                      onClick={() =>
                                        handleSendEmailDropdown(user, "reminder", groupKey)
                                      }
                                      icon={<EmailIcon color="blue.500" />}
                                    >
                                      Send Signing Reminder
                                    </MenuItem>
                                    <MenuItem
                                      onClick={() =>
                                        handleSendEmailDropdown(user, "help", groupKey)
                                      }
                                      icon={<InfoOutlineIcon color="teal.500" />}
                                    >
                                      Offer Help
                                    </MenuItem>
                                  </>
                                )}
                                {groupKey === "both" && (
                                  <>
                                    <MenuItem
                                      onClick={() =>
                                        handleSendEmailDropdown(user, "demo", groupKey)
                                      }
                                      icon={<StarIcon color="yellow.500" />}
                                    >
                                      Offer a Demo
                                    </MenuItem>
                                    <MenuItem
                                      onClick={() =>
                                        handleSendEmailDropdown(user, "help", groupKey)
                                      }
                                      icon={<InfoOutlineIcon color="blue.500" />}
                                    >
                                      Check-in / Help
                                    </MenuItem>
                                  </>
                                )}
                              </MenuList>
                            </Menu>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            ))}

            {/* CSV Export Modal */}
            <Modal isOpen={csvModalOpen} onClose={() => setCsvModalOpen(false)} size="lg">
              <ModalOverlay />
              <ModalContent>
                <ModalHeader>Export CSV for {sectionLabels[csvModalGroupKey]}</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                  <Text mb={2}>Select columns to export:</Text>
                  <VStack align="start" mb={4}>
                    {csvModalHeaders.map((h) => (
                      <Checkbox
                        key={h.key}
                        isChecked={csvModalSelectedHeaders.includes(h.key)}
                        onChange={(e) => {
                          setCsvModalSelectedHeaders((prev) =>
                            e.target.checked
                              ? [...prev, h.key]
                              : prev.filter((k) => k !== h.key)
                          );
                        }}
                      >
                        {h.label}
                      </Checkbox>
                    ))}
                  </VStack>
                  <Text mb={2}>Choose which users to export:</Text>
                  <Stack direction="row" spacing={4}>
                    <Button
                      variant={csvModalFilter === "all" ? "solid" : "outline"}
                      colorScheme="teal"
                      onClick={() => setCsvModalFilter("all")}
                    >
                      All users
                    </Button>
                    <Button
                      variant={csvModalFilter === "orders0" ? "solid" : "outline"}
                      colorScheme="red"
                      onClick={() => setCsvModalFilter("orders0")}
                    >
                      Users with orders = 0
                    </Button>
                  </Stack>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme="blue"
                    onClick={handleExportCsv}
                    isDisabled={csvModalSelectedHeaders.length === 0}
                    mr={3}
                  >
                    Export CSV
                  </Button>
                  <Button variant="ghost" onClick={() => setCsvModalOpen(false)}>
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Modal>
          </TabPanel>

          <TabPanel>{renderActiveAccountsTab()}</TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

// ===== Helpers used in Active Accounts CSV export (kept) =====
const exportActiveAccountsCsv = (sectionKey, sectionData, sectionName) => {
  const headers = [
    { key: "email", label: "Email" },
    { key: "facilityName", label: "Facility Name" },
    { key: "address", label: "Address" },
    { key: "phoneNumber", label: "Phone Number" },
    { key: "npi", label: "NPI" },
    { key: "orderCount", label: "Order Count" },
    { key: "lastOrderDate", label: "Last Order Date" },
    { key: "daysSinceLastOrder", label: "Days Since Last Order" },
    { key: "daysSinceSigned", label: "Days Since Signed" },
    { key: "ivrCount", label: "IVRs Sent" },
    { key: "UserId", label: "User ID" },
  ];

  const rows = sectionData.map((user) => ({
    email: user.email,
    facilityName: user.facilityName || "—",
    address: user.address || "—",
    phoneNumber: user.phoneNumber || "—",
    npi: user.npi || "—",
    orderCount: user.orderCount || 0,
    lastOrderDate: user.lastOrderDate ? formatDateTime(user.lastOrderDate) : "—",
    daysSinceLastOrder: user.daysSinceLastOrder ?? "—",
    daysSinceSigned: user.daysSinceSigned ?? "—",
    ivrCount: user._ivrCount ?? "—", // not used; left as placeholder
    UserId: user.UserId || user.userId,
  }));

  exportToCsv(`active_accounts_${sectionKey}_${Date.now()}.csv`, headers, rows);
};

export default UserAgreementStatusPage;
